/**
 * 렌더 중 예외를 붙잡는다 — 없으면 React 는 화면 전체를 떼어 내고 **흰 화면만** 남긴다.
 *
 * 흰 화면은 사용자에게 아무것도 알려 주지 않고, 신고는 "그냥 하얘져요" 로 온다. 여기서 잡아
 * ① 기록을 남기고 ② **오류 번호**를 보여 주고 ③ "무엇을 하려던 중이었나" 를 받는다.
 *
 * ## 왜 오류 원문을 화면에 쓰지 않나
 *
 * 오류 메시지에는 테이블·컬럼 이름이나 값이 섞여 있을 수 있고, **이 화면은 누가 볼지 모른다**(공개
 * 서비스·화면 공유·공용 PC). 무엇이 났는지는 기록에서 본다 — 화면에는 그 기록을 가리키는 **번호**만
 * 둔다. 형제 프로젝트 livesql 의 ErrorBoundary 가 같은 이유로 같은 선택을 했다.
 *
 * ## 왜 신고 칸이 있나
 *
 * 자동 수집이 얻을 수 없는 것이 하나 있다 — **무엇을 하려던 중이었나.** 스택은 어디서 터졌는지는
 * 말해 주지만 왜 거기까지 갔는지는 말해 주지 않는다. 그 한 줄이 재현의 출발점이다.
 *
 * ## 경계가 스스로 깨지면 안 된다
 *
 * React 는 `componentDidCatch` 안에서 던져진 예외를 **다시 던진다.** 그러면 안내 화면도 못 뜬 채
 * 흰 화면으로 돌아간다 — 고치려던 바로 그 상태다. 그래서 기록 호출을 try 로 감싼다.
 *
 * 이벤트 처리기·비동기 안에서 난 예외는 여기로 오지 않는다. 그쪽은 services/eventLog 의
 * installGlobalErrorHandlers(error / unhandledrejection)가 받는다. 둘 다 있어야 구멍이 없다.
 */
import React from 'react';
import { logError, reportError } from '../../services/eventLog';
import i18n from '../../i18n';

// 클래스 컴포넌트라 훅을 쓰지 않는다 — 안내 화면은 오류가 난 순간에 그려지므로 그때의 언어로 충분하다
const t = (key: string, opts?: Record<string, unknown>) => String(i18n.t(`layout:err.${key}`, opts as any));

interface Props { children: React.ReactNode }
interface State {
  error: Error | null;
  errorId: string;
  note: string;
  sending: boolean;
  sent: boolean;
}

class AppErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null, errorId: '', note: '', sending: false, sent: false };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // 경계가 스스로 깨지지 않게 — logError 는 원래 던지지 않지만, 그 계약에 안내 화면을 걸지 않는다.
    try {
      const componentStack = (info.componentStack || '').slice(0, 4000);
      // componentStack 은 **따로** 싣는다. error.stack 에 덮어쓰면 어느 줄에서 났는지가 사라진다.
      const id = logError('ui.crash', error, { where: 'render', detail: { componentStack } });
      this.setState({ errorId: id });
    } catch { /* 기록 실패가 안내를 막지 않는다 */ }
  }

  /** 번호와 맥락만 복사한다 — 오류 원문·스택은 넣지 않는다(기록에서 번호로 찾는다) */
  private copy = (): void => {
    const { errorId } = this.state;
    const text = [
      t('copyId', { id: errorId || t('copyIdFailed') }),
      t('copyTime', { time: new Date().toISOString() }),
      t('copyScreen', { path: window.location.pathname }),
    ].join('\n');
    void navigator.clipboard?.writeText(text);
  };

  private send = async (): Promise<void> => {
    const { errorId, note } = this.state;
    if (!errorId || this.state.sending) return;
    this.setState({ sending: true });
    const ok = await reportError(errorId, note);
    this.setState({ sending: false, sent: ok });
  };

  render(): React.ReactNode {
    const { error, errorId, note, sending, sent } = this.state;
    if (!error) return this.props.children;
    return (
      <div className="h-screen w-full flex items-center justify-center bg-gray-50 p-6">
        <div className="max-w-xl w-full bg-white border border-red-200 rounded-lg shadow p-5">
          <div className="text-lg font-semibold text-red-700 mb-1">{t('title')}</div>
          <p className="text-sm text-gray-600 mb-3">
            {t('intro')}
          </p>

          {/* 화면에 두는 것은 오류 원문이 아니라 기록을 가리키는 번호다 */}
          <div className="rounded border border-gray-200 bg-gray-50 px-3 py-2 mb-3">
            <div className="text-[0.6875rem] text-gray-500">{t('idHint')}</div>
            <div className="font-mono text-base text-gray-900 select-all">{errorId || t('logFailed')}</div>
          </div>

          {sent ? (
            <div className="text-sm text-green-700 bg-green-50 border border-green-200 rounded px-3 py-2 mb-3">
              {t('thanks', { id: errorId })}
            </div>
          ) : (
            <div className="mb-3">
              <label className="block text-xs text-gray-600 mb-1" htmlFor="of-err-note">
                {t('whatDoing')}<span className="text-gray-400">{t('optional')}</span>
              </label>
              <div className="flex gap-2">
                <input id="of-err-note" value={note} maxLength={200}
                  onChange={(e) => this.setState({ note: e.target.value })}
                  placeholder={t('notePh')}
                  className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded" />
                <button type="button" onClick={this.send} disabled={!errorId || sending}
                  className="text-sm px-3 py-1.5 rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50">
                  {sending ? t('sending') : t('report')}
                </button>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <button type="button" onClick={this.copy}
              className="text-sm px-3 py-1.5 rounded border border-gray-300 text-gray-700 hover:bg-gray-100">
              {t('copy')}
            </button>
            <button type="button" onClick={() => this.setState({ error: null, errorId: '', note: '', sent: false })}
              className="text-sm px-3 py-1.5 rounded border border-gray-300 text-gray-700 hover:bg-gray-100">
              {t('retry')}
            </button>
            <button type="button" onClick={() => window.location.reload()}
              className="text-sm px-3 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700">
              {t('reload')}
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default AppErrorBoundary;
